/**
 * DI-3-F — the manifest and the poll (story §2.1 `manifest.ts`; locked
 * the-manifest-carries-every-file-of-the-session).
 *
 * The manifest of an upload PASS lists every file the pass is about — those
 * sent now AND those the ledger already holds from an earlier tab — as
 * `{filename, sizeBytes, md5 (hex), capturedAtMs}` from the parsed rows (no
 * file is read again). The gateway verifies each against its blob (size +
 * MD5) in the ingest job and writes `blob_state = present`; when every file
 * of the manifest verifies and the set is approved it promotes the set to
 * PROCESSING by itself (DI-3-B / DI-3-Bb — no second click).
 *
 * The job id is a function of the manifest and the attempt (`intakeJobId({da,
 * 'ingest', inputHash, attempt})`, inspection-3/v1 2.3.2 — DI-3-P3): completing
 * the SAME manifest while its run is queued / running, or after it
 * COMPLETED, is ONE job — the poll finds that run (DI-1-6's dedup; used by a
 * resume that died after its complete); completing it after its run ended
 * FAILED starts a new attempt. A retry pass therefore completes exactly the
 * files the server has not verified yet (`passManifest` — the refused ones,
 * re-sent), nothing else.
 *
 * The poll (`getUploadSession`, every `POLL_MS`) ends on `COMPLETED` /
 * `FAILED` — the DI-1-6 finding "poll before finalize".
 */
import type {
  AuxManifestEntry,
  IngestError,
  UploadManifestEntry,
  UploadSessionStatus,
} from "../api/uploadSessions.js";
import { md5Hex } from "./md5.js";

/** The poll's interval (story §5 shape choice: 5 s). */
export const POLL_MS = 5_000;

export interface UploadFile {
  /** The blob name = the round trip's wire name (`images.image_filename`). */
  filename: string;
  sizeBytes: number;
  md5Hex: string;
  capturedAtMs: number | null;
  /** The row of this tab's index the bytes come from. */
  index: number;
}

export function manifestOf(
  files: readonly UploadFile[],
): UploadManifestEntry[] {
  return files.map((f) => ({
    filename: f.filename,
    sizeBytes: f.sizeBytes,
    md5: md5Hex(f.md5Hex),
    capturedAtMs: f.capturedAtMs,
  }));
}

/**
 * DI-3-S75 §3.1 — a folder's `*_pos.txt` that rides the set's upload session
 * to `data-acquisitions/<da>/aux/<relativeFolder>/<filename>`; never an image,
 * never in the ledger (it is sent again with every manifest — the ingest
 * dedupes on the path).
 */
export interface AuxUpload {
  filename: string;
  /** The folder of the set's images it lies in ("" = the top level). */
  relativeFolder: string;
  file: Blob;
}

/** DI-3-S75 — an aux file sent: its manifest entry (`md5` = lower-case hex). */
export function auxManifestEntry(
  a: Pick<AuxUpload, "filename" | "relativeFolder">,
  sizeBytes: number,
  md5: string,
): AuxManifestEntry {
  return {
    filename: a.filename,
    relativeFolder: a.relativeFolder,
    sizeBytes,
    md5: md5Hex(md5),
  };
}

/**
 * The manifest of the next pass: exactly the files the server has not
 * verified yet, in the set's order (after a FAILED run: the refused ones).
 */
export function passManifest(
  all: readonly UploadFile[],
  verified: ReadonlySet<string>,
): UploadFile[] {
  return all.filter((f) => !verified.has(f.filename));
}

/** The codes after which the file is sent again (the others fail the pass). */
export const RESEND_CODES: ReadonlySet<string> = new Set([
  "blob_absent",
  "blob_mismatch",
]);

export interface PollOutcome {
  state: "COMPLETED" | "FAILED";
  status: UploadSessionStatus;
  /** Files the ingest refused (by name). */
  refused: string[];
}

/** The refused file names of a status (errors without a name count once each). */
export function refusedFiles(errors: readonly IngestError[]): {
  named: string[];
  unnamed: number;
} {
  const named = new Set<string>();
  let unnamed = 0;
  for (const e of errors) {
    if (e.filename) named.add(e.filename);
    else unnamed += 1;
  }
  return { named: [...named], unnamed };
}

/** Poll until the session is COMPLETED or FAILED. */
export async function pollUntilDone(opts: {
  poll: () => Promise<UploadSessionStatus>;
  onProgress?: (s: UploadSessionStatus) => void;
  sleep: (ms: number) => Promise<void>;
  intervalMs?: number;
}): Promise<PollOutcome> {
  for (;;) {
    const s = await opts.poll();
    opts.onProgress?.(s);
    if (
      s.state === "COMPLETED" ||
      s.state === "FAILED" ||
      s.state === "EXPIRED"
    )
      return {
        state: s.state === "COMPLETED" ? "COMPLETED" : "FAILED",
        status: s,
        refused: refusedFiles(s.errors).named,
      };
    await opts.sleep(opts.intervalMs ?? POLL_MS);
  }
}
